import { useState, useEffect } from "react";
import { Phone, Menu, X } from "lucide-react";
import { CONTACT } from "@/data";

const links = [
  { label: "Services", id: "services" },
  { label: "Projects", id: "projects" },
  { label: "About", id: "about" },
  { label: "Reviews", id: "testimonials" },
  { label: "Areas", id: "areas" },
  { label: "Contact", id: "contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#1c1c20]/85 backdrop-blur-xl border-b border-white/10 shadow-lg shadow-black/30"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 md:h-20">
        <button data-testid="nav-logo" onClick={() => scrollTo("hero")} className="flex items-center gap-2.5">
          <span className="logo-ring relative inline-flex p-[2.5px] rounded-[11px] overflow-hidden">
            <img
              src="/mjl-logo.jpeg"
              alt="MJL Maintenance & Locks Ltd"
              className="relative z-[1] h-10 md:h-12 w-auto rounded-lg"
            />
          </span>
        </button>

        <div className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}-link`}
              onClick={() => scrollTo(l.id)}
              className="nav-underline text-sm font-semibold text-gray-300 hover:text-white transition-colors"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-5">
          <a
            data-testid="nav-phone-text"
            href={`tel:${CONTACT.phoneRaw}`}
            className="inline-flex items-center gap-2 text-sm font-semibold text-gray-200 hover:text-white transition-colors"
          >
            <Phone className="w-4 h-4 text-[#E63946]" /> {CONTACT.phone}
          </a>
          <button
            data-testid="nav-call-button"
            onClick={() => scrollTo("contact")}
            className="btn-press inline-flex items-center gap-2 rounded-full bg-[#E63946] hover:bg-[#D62828] text-white px-5 py-2.5 text-sm font-bold shadow-lg shadow-[#E63946]/25"
          >
            Get a Free Quote
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 text-white"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden bg-[#1c1c20]/97 backdrop-blur-xl border-b border-white/10 px-4 py-4 space-y-1">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              className="block w-full text-left px-3 py-3 rounded-lg text-base font-semibold text-gray-200 hover:bg-white/5"
            >
              {l.label}
            </button>
          ))}
          <a
            href={`tel:${CONTACT.phoneRaw}`}
            className="flex items-center justify-center gap-2 mt-2 rounded-full bg-[#E63946] text-white px-5 py-3 font-bold"
          >
            <Phone className="w-4 h-4" /> Call {CONTACT.phone}
          </a>
        </div>
      )}
    </header>
  );
};
